import React, { Component } from "react";
import agent from "../agent";

class SecureVideoPlayer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      videoData: null,
      isLoading: true,
      error: null,
      isPlaying: false,
      isMuted: false,
      volume: 1.0,
      currentTime: 0,
      duration: 0,
      quality: "auto",
      availableQualities: [],
      showControls: true,
      analyticsEnabled: true,
    };

    this.videoRef = React.createRef();
    this.playerContainer = React.createRef();
    this.controlsTimer = null;
    this.analyticsTimer = null;
    this.lastAnalyticsTime = 0;
  }

  componentDidMount() {
    if (this.props.videoId) {
      this.loadSecureVideo();
    }

    // Hide controls after 3 seconds of inactivity
    this.setupControlsHiding();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.videoId !== this.props.videoId && this.props.videoId) {
      this.loadSecureVideo();
    }
  }

  componentWillUnmount() {
    this.cleanup();
  }

  cleanup() {
    if (this.controlsTimer) {
      clearTimeout(this.controlsTimer);
    }
    if (this.analyticsTimer) {
      clearInterval(this.analyticsTimer);
    }

    // Send final analytics event
    if (this.state.videoData && this.state.currentTime > 0) {
      this.trackAnalytics("complete", {
        final_position: this.state.currentTime,
        total_duration: this.state.duration,
        completion_percentage: (
          (this.state.currentTime / this.state.duration) *
          100
        ).toFixed(2),
      });
    }
  }

  async loadSecureVideo() {
    try {
      this.setState({ isLoading: true, error: null });

      const response = await agent.Video.getSecureVideo(this.props.videoId);

      // Handle case where response is undefined or not successful
      if (!response) {
        throw new Error("No response received from server");
      }

      if (response.success) {
        this.setState({
          videoData: response.data,
          isLoading: false,
        });

        // Initialize analytics tracking
        this.trackAnalytics("video_request");
        this.startAnalyticsTracking();
      } else {
        throw new Error(
          response.error || response.message || "Failed to load video"
        );
      }
    } catch (error) {
      console.error("Error loading secure video:", error);

      // Check if this is a network/server error
      let errorMessage = error.message || "Failed to load video";

      // If it's a token/auth issue, provide helpful message
      if (
        errorMessage.includes("access token") ||
        errorMessage.includes("Authentication required")
      ) {
        errorMessage =
          "Video authentication failed. OAuth connection may be required.";
      }

      this.setState({
        error: errorMessage,
        isLoading: false,
      });

      // Call onError prop if provided (for parent component fallback)
      if (this.props.onError) {
        this.props.onError(errorMessage);
      }
    }
  }

  setupControlsHiding() {
    if (this.playerContainer.current) {
      this.playerContainer.current.addEventListener(
        "mousemove",
        this.showControlsTemporarily
      );
      this.playerContainer.current.addEventListener(
        "mouseleave",
        this.hideControls
      );
    }
  }

  showControlsTemporarily = () => {
    this.setState({ showControls: true });

    if (this.controlsTimer) {
      clearTimeout(this.controlsTimer);
    }

    this.controlsTimer = setTimeout(() => {
      if (this.state.isPlaying) {
        this.setState({ showControls: false });
      }
    }, 3000);
  };

  hideControls = () => {
    if (this.state.isPlaying) {
      this.setState({ showControls: false });
    }
  };

  startAnalyticsTracking() {
    if (!this.state.analyticsEnabled) return;

    // Track viewing progress every 10 seconds
    this.analyticsTimer = setInterval(() => {
      if (this.state.isPlaying && this.videoRef.current) {
        const currentTime = this.videoRef.current.currentTime;

        // Only track if user has progressed significantly
        if (currentTime - this.lastAnalyticsTime >= 10) {
          this.trackAnalytics("progress", {
            position: currentTime,
            duration_watched: currentTime - this.lastAnalyticsTime,
          });
          this.lastAnalyticsTime = currentTime;
        }
      }
    }, 10000);
  }

  async trackAnalytics(action, metadata = {}) {
    if (!this.state.analyticsEnabled || !this.state.videoData) return;

    try {
      await agent.Video.trackAnalytics({
        video_id: this.props.videoId,
        action: action,
        duration: this.state.duration || 0,
        position: this.state.currentTime || 0,
        quality: this.state.quality,
        metadata: {
          ...metadata,
          timestamp: new Date().toISOString(),
          user_agent: navigator.userAgent,
          screen_size: `${window.screen.width}x${window.screen.height}`,
          player_size: this.videoRef.current
            ? `${this.videoRef.current.videoWidth}x${this.videoRef.current.videoHeight}`
            : null,
        },
      });
    } catch (error) {
      console.warn("Failed to track video analytics:", error);
    }
  }

  handlePlay = () => {
    if (this.videoRef.current) {
      this.videoRef.current.play();
      this.setState({ isPlaying: true });
      this.trackAnalytics("play");
    }
  };

  handlePause = () => {
    if (this.videoRef.current) {
      this.videoRef.current.pause();
      this.setState({ isPlaying: false, showControls: true });
      this.trackAnalytics("pause");
    }
  };

  handleMute = () => {
    if (this.videoRef.current) {
      const newMuted = !this.state.isMuted;
      this.videoRef.current.muted = newMuted;
      this.setState({ isMuted: newMuted });
      this.trackAnalytics(newMuted ? "mute" : "unmute");
    }
  };

  handleVolumeChange = (event) => {
    const newVolume = parseFloat(event.target.value);
    if (this.videoRef.current) {
      this.videoRef.current.volume = newVolume;
      this.setState({ volume: newVolume, isMuted: newVolume === 0 });
    }
  };

  handleSeek = (event) => {
    const seekTime = parseFloat(event.target.value);
    if (this.videoRef.current) {
      this.videoRef.current.currentTime = seekTime;
      this.setState({ currentTime: seekTime });
      this.trackAnalytics("seek", {
        from_position: this.state.currentTime,
        to_position: seekTime,
      });
    }
  };

  handleTimeUpdate = () => {
    if (this.videoRef.current) {
      this.setState({
        currentTime: this.videoRef.current.currentTime,
        duration: this.videoRef.current.duration || 0,
      });
    }
  };

  handleQualityChange = (quality) => {
    this.setState({ quality });
    this.trackAnalytics("quality_change", { new_quality: quality });
    // In a real implementation, you would switch video sources here
  };

  formatTime(seconds) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  }

  render() {
    const {
      videoData,
      isLoading,
      error,
      isPlaying,
      isMuted,
      volume,
      currentTime,
      duration,
      showControls,
    } = this.state;

    if (isLoading) {
      return (
        <div className="secure-video-player loading">
          <div className="loading-spinner">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading video...</span>
            </div>
            <p className="mt-2">Loading secure video...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="secure-video-player error">
          <div className="alert alert-danger">
            <h5>Video Load Error</h5>
            <p>{error}</p>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => this.loadSecureVideo()}
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    if (!videoData) {
      return (
        <div className="secure-video-player">
          <div className="alert alert-info">No video data available</div>
        </div>
      );
    }

    return (
      <div
        className="secure-video-player"
        ref={this.playerContainer}
        style={{ position: "relative", backgroundColor: "#000" }}
      >
        <video
          ref={this.videoRef}
          className="video-element"
          style={{
            width: "100%",
            height: "auto",
            maxHeight: "70vh",
          }}
          onPlay={() => this.setState({ isPlaying: true })}
          onPause={() => this.setState({ isPlaying: false })}
          onTimeUpdate={this.handleTimeUpdate}
          onLoadedMetadata={this.handleTimeUpdate}
          onError={(e) => {
            console.error("Video error:", e);
            this.setState({ error: "Video playback error" });
            this.trackAnalytics("error", { error: e.target.error });
          }}
        >
          {videoData.files && videoData.files.length > 0 && (
            <source
              src={videoData.player_embed_url || videoData.files[0].secure_url}
              type="video/mp4"
            />
          )}
          Your browser does not support the video tag.
        </video>

        {/* Video Info Overlay */}
        <div
          className="video-info"
          style={{
            position: "absolute",
            top: "10px",
            left: "10px",
            color: "white",
            backgroundColor: "rgba(0,0,0,0.7)",
            padding: "8px 12px",
            borderRadius: "4px",
            fontSize: "14px",
            opacity: showControls ? 1 : 0,
            transition: "opacity 0.3s",
          }}
        >
          <div className="video-title">{videoData.title}</div>
          {videoData.live_stream && (
            <span className="badge badge-danger badge-pill ml-2">LIVE</span>
          )}
        </div>

        {/* Custom Controls */}
        <div
          className="video-controls"
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            background: "linear-gradient(transparent, rgba(0,0,0,0.8))",
            padding: "20px 15px 15px",
            opacity: showControls ? 1 : 0,
            transition: "opacity 0.3s",
          }}
        >
          {/* Progress Bar */}
          <div className="progress-container mb-3">
            <input
              type="range"
              className="form-range"
              min="0"
              max={duration || 0}
              value={currentTime}
              onChange={this.handleSeek}
              style={{ width: "100%" }}
            />
            <div className="time-display d-flex justify-content-between text-white small mt-1">
              <span>{this.formatTime(currentTime)}</span>
              <span>{this.formatTime(duration)}</span>
            </div>
          </div>

          {/* Control Buttons */}
          <div className="control-buttons d-flex align-items-center">
            {/* Play/Pause */}
            <button
              className="btn btn-sm btn-outline-light me-2"
              onClick={isPlaying ? this.handlePause : this.handlePlay}
            >
              <i
                className={`bi ${isPlaying ? "bi-pause-fill" : "bi-play-fill"}`}
              ></i>
            </button>

            {/* Mute/Unmute */}
            <button
              className="btn btn-sm btn-outline-light me-2"
              onClick={this.handleMute}
            >
              <i
                className={`bi ${
                  isMuted || volume === 0
                    ? "bi-volume-mute-fill"
                    : "bi-volume-up-fill"
                }`}
              ></i>
            </button>

            {/* Volume */}
            <input
              type="range"
              className="form-range me-3"
              min="0"
              max="1"
              step="0.1"
              value={volume}
              onChange={this.handleVolumeChange}
              style={{ width: "80px" }}
            />

            {/* Quality Selector */}
            {videoData.files && videoData.files.length > 1 && (
              <select
                className="form-select form-select-sm me-2"
                style={{
                  width: "auto",
                  backgroundColor: "rgba(255,255,255,0.2)",
                  color: "white",
                  border: "1px solid rgba(255,255,255,0.3)",
                }}
                value={this.state.quality}
                onChange={(e) => this.handleQualityChange(e.target.value)}
              >
                <option value="auto">Auto</option>
                {videoData.files.map((file, index) => (
                  <option key={index} value={file.quality}>
                    {file.quality} ({file.height}p)
                  </option>
                ))}
              </select>
            )}

            {/* Fullscreen */}
            <button
              className="btn btn-sm btn-outline-light"
              onClick={() => {
                if (this.playerContainer.current.requestFullscreen) {
                  this.playerContainer.current.requestFullscreen();
                }
              }}
            >
              <i className="bi bi-fullscreen"></i>
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default SecureVideoPlayer;
